import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Upload, FileText, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, uploadFile } from "@/lib/api";

const EMPTY = {
  numero: "", data: "", produttore: "", trasportatore: "", destinatario: "",
  codice_cer: "", descrizione: "", quantita: "", file_id: "", file_nome: "",
};

export default function FirModal({ open, onOpenChange, cantiereId, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setForm((f) => ({ ...f, file_id: res.file_id, file_nome: res.filename }));
      toast.success("PDF caricato.");
    } catch (err) { toast.error(err.message || "Caricamento non riuscito."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.numero.trim() && !form.data) return toast.error("Inserisci almeno Numero FIR o Data.");
    setSaving(true);
    try {
      const payload = { ...form, cantiere_id: cantiereId };
      if (isEdit) await api.put(`/fir/${initial.id}`, payload);
      else await api.post("/fir", payload);
      toast.success(isEdit ? "FIR aggiornato." : "FIR creato.");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(err.message || "Salvataggio non riuscito."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fir-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica FIR" : "Nuovo FIR"}</DialogTitle>
          <DialogDescription>Formulario di Identificazione Rifiuti per la commessa.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Numero FIR</Label>
              <Input value={form.numero} data-testid="fir-numero" onChange={(e) => set("numero", e.target.value)} placeholder="es. FIR-00123" />
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" value={form.data} data-testid="fir-data" onChange={(e) => set("data", e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Produttore</Label>
            <Input value={form.produttore} data-testid="fir-produttore" onChange={(e) => set("produttore", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Trasportatore</Label>
            <Input value={form.trasportatore} data-testid="fir-trasportatore" onChange={(e) => set("trasportatore", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Destinatario</Label>
            <Input value={form.destinatario} data-testid="fir-destinatario" onChange={(e) => set("destinatario", e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Codice CER</Label>
              <Input value={form.codice_cer} data-testid="fir-cer" onChange={(e) => set("codice_cer", e.target.value)} placeholder="es. 17 09 04" />
            </div>
            <div className="space-y-1.5">
              <Label>Quantità</Label>
              <Input value={form.quantita} data-testid="fir-quantita" onChange={(e) => set("quantita", e.target.value)} placeholder="es. 1.200 kg" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Descrizione rifiuto</Label>
            <Textarea rows={2} value={form.descrizione} data-testid="fir-descrizione" onChange={(e) => set("descrizione", e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>PDF del formulario</Label>
            {form.file_id ? (
              <div className="flex items-center gap-3 rounded-md border bg-secondary/40 px-3 py-2">
                <FileText className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="text-sm truncate flex-1">{form.file_nome || "documento.pdf"}</span>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid="fir-file-remove"
                  onClick={() => setForm((f) => ({ ...f, file_id: "", file_nome: "" }))}><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <label className="flex items-center gap-2 cursor-pointer text-sm rounded-md border border-dashed px-3 py-2 hover:bg-secondary w-fit" data-testid="fir-file-upload">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Caricamento..." : "Carica PDF"}
                <input type="file" accept="application/pdf" className="hidden" disabled={uploading} onChange={onUpload} />
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="fir-save-button"
            className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
